import React, { useContext } from 'react';
import { IntegrationSettingsInput } from '../../../../../../../types/integration_settings';
import { ActionModalBody, ActionModalFooter } from '../../../../../../components/Modal/templates/ActionModal';
import TextInput from '../../../../../Common/TextInput';
import { Divider } from '../../../../../../components/Divider';
import { CancelButton, DeleteButton, Button, ButtonVariant } from '../../../../../../components/Button';
import { t } from '../../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { UserContext } from '../../../../../../../context/UserContext';
import { useToast } from '../../../../../../../providers/ToastProvider';
import {
  TeamleaderFetchReferenceDataResponse,
  TeamleaderFetchProductsVariables,
  TeamleaderMutations,
  TeamleaderFetchProductsResponse,
  TeamleaderFetchReferenceDataVariables,
  TeamleaderCreateCustomFieldsResponse,
  TeamleaderCreateCustomFieldsVariables,
  TeamleaderCreateStockLocationsResponse,
  TeamleaderCreateStockLocationsVariables,
  TeamleaderCreateBinsResponse,
  TeamleaderCreateBinsVariables,
  TeamleaderFetchContactsResponse,
  TeamleaderFetchContactsVariables,
  TeamleaderCreateWebhooksResponse,
  TeamleaderCreateWebhooksVariables,
} from '../../../../../../../graphql/teamleader.graphql';
import { ToastType } from '../../../../../../components/Toast';
import useMutation from '../../../../../../../hooks/useMutation';

interface TeamleaderIntegrationInfoPaneProps {
  input: IntegrationSettingsInput;
  setInput: (input: IntegrationSettingsInput) => void;

  loading: boolean;

  handleCancel: () => void;
  handleDelete: () => void;
}

export default function TeamleaderIntegrationInfoPane({
  input,
  setInput,
  loading,
  handleCancel,
  handleDelete,
}: TeamleaderIntegrationInfoPaneProps) {
  const { showToast } = useToast();

  const { companyUsers } = useContext(UserContext);

  const complete = () =>
    showToast({
      type: ToastType.success,
      title: t(TranslationKey.complete).singular.upper,
    });

  const fail = () =>
    showToast({
      type: ToastType.error,
      title: t(TranslationKey.errorOccurred).singular.upper,
    });

  const [fetchReferenceData, { loading: loadingReferenceData }] = useMutation<
    TeamleaderFetchReferenceDataResponse,
    TeamleaderFetchReferenceDataVariables
  >(TeamleaderMutations.fetchReferenceData, {
    onCompleted: data => (data.teamleaderFetchReferenceData ? complete() : fail()),
  });

  const [fetchProducts, { loading: loadingProducts }] = useMutation<
    TeamleaderFetchProductsResponse,
    TeamleaderFetchProductsVariables
  >(TeamleaderMutations.fetchProducts, {
    onCompleted: data => (data.teamleaderFetchProducts ? complete() : fail()),
  });

  const [fetchContacts, { loading: loadingContacts }] = useMutation<
    TeamleaderFetchContactsResponse,
    TeamleaderFetchContactsVariables
  >(TeamleaderMutations.fetchContacts, {
    onCompleted: data => (data.teamleaderFetchContacts ? complete() : fail()),
  });

  const [createCustomFields, { loading: loadingCustomFields }] = useMutation<
    TeamleaderCreateCustomFieldsResponse,
    TeamleaderCreateCustomFieldsVariables
  >(TeamleaderMutations.createCustomFields, {
    onCompleted: data => (data.teamleaderCreateCustomFields ? complete() : fail()),
  });

  const [createStockLocations, { loading: loadingStockLocations }] = useMutation<
    TeamleaderCreateStockLocationsResponse,
    TeamleaderCreateStockLocationsVariables
  >(TeamleaderMutations.createStockLocations, {
    onCompleted: data => (data.teamleaderCreateStockLocations ? complete() : fail()),
  });

  const [createWebhooks, { loading: loadingWebhooks }] = useMutation<
    TeamleaderCreateWebhooksResponse,
    TeamleaderCreateWebhooksVariables
  >(TeamleaderMutations.createWebhooks, {
    onCompleted: data => (data.teamleaderCreateWebhooks ? complete() : fail()),
  });

  const [createBins, { loading: loadingBins }] = useMutation<
    TeamleaderCreateBinsResponse,
    TeamleaderCreateBinsVariables
  >(TeamleaderMutations.createBins, {
    onCompleted: data => (data.teamleaderCreateBins ? complete() : fail()),
  });

  const isLoading =
    loading ||
    loadingReferenceData ||
    loadingProducts ||
    loadingContacts ||
    loadingCustomFields ||
    loadingStockLocations ||
    loadingWebhooks ||
    loadingBins;

  return (
    <>
      <ActionModalBody>
        <div className='flex flex-col gap-2'>
          <div>
            <TextInput
              disabled={true}
              label={t(TranslationKey.company).singular.label}
              onChange={() => {}}
              value={input.externalInfo}
            />
          </div>
          <div>
            <TextInput
              disabled={true}
              label={t(TranslationKey.administrator).singular.label}
              onChange={() => {}}
              value={companyUsers.get(input.userId || '')?.email}
            />
          </div>
          <div className='flex gap-2'>
            <Button
              disabled={isLoading}
              loading={loadingReferenceData}
              onClick={() => fetchReferenceData()}
              text={t(TranslationKey.teamleaderFetchReferenceData).plural.label}
            />
            <Button
              disabled={isLoading}
              loading={loadingProducts}
              onClick={() => fetchProducts()}
              text={t(TranslationKey.teamleaderFetchProduct).plural.label}
            />
            <Button
              loading={loadingContacts}
              onClick={() => fetchContacts()}
              text={t(TranslationKey.teamleaderFetchContact).plural.label}
            />
          </div>
          <div className='my-2'>
            <Divider />
          </div>
          <div className='flex gap-2'>
            <Button
              disabled={isLoading}
              variant={ButtonVariant.secondary}
              loading={loadingCustomFields}
              onClick={() => createCustomFields()}
              text={t(TranslationKey.teamleaderCreateCustomField).plural.label}
            />
            <Button
              disabled={isLoading}
              variant={ButtonVariant.secondary}
              loading={loadingStockLocations}
              onClick={() => createStockLocations()}
              text={t(TranslationKey.teamleaderCreateStockLocation).plural.label}
            />
            <Button
              disabled={isLoading}
              variant={ButtonVariant.secondary}
              loading={loadingBins}
              onClick={() => createBins()}
              text={t(TranslationKey.teamleaderCreateBin).plural.label}
            />
            <Button
              disabled={isLoading}
              variant={ButtonVariant.secondary}
              loading={loadingWebhooks}
              onClick={() => createWebhooks()}
              text={t(TranslationKey.teamleaderCreateWebhook).plural.label}
            />
          </div>
        </div>
      </ActionModalBody>
      <Divider />
      <ActionModalFooter>
        <div className='flex justify-between gap-2'>
          <DeleteButton loading={loading} disabled={isLoading} onClick={handleDelete} />
          <CancelButton disabled={isLoading} onClick={handleCancel} />
        </div>
      </ActionModalFooter>
    </>
  );
}
